// src/utils/languageDetection.ts

import { getStrapiURL } from "./utils.tsx";
import { isServer, getApiToken } from "./env";

interface LegacyNavigator {
  userLanguage?: string;
}

/**
 * Function to detect user's country by IP using Strapi API
 * @returns Promise that resolves to the country code
 */
export const getCountryByIP = async (): Promise<string> => {
  try {
    const url = getStrapiURL("/api/geolocation/ip");

    // On server: add token
    const headers: HeadersInit = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    if (isServer()) {
      headers.Authorization = "Bearer " + getApiToken();
    }

    // Request to Strapi API (through proxy on client)
    const response = await fetch(url, { headers });

    // Check Content-Type of response
    const contentType = response.headers.get("Content-Type");
    if (!contentType || !contentType.includes("application/json")) {
      console.error("API response is not JSON:", contentType);
      throw new Error("API response is not in JSON format");
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch location data: ${response.status}`);
    }

    const data = await response.json();
    console.log("Geolocation response:", data);

    // Return country code (e.g., 'UA', 'US')
    return data.country || "US";
  } catch (error) {
    console.error("Error fetching location by IP:", error);

    // Try alternative API for country detection
    try {
      console.log("Trying alternative IP API...");
      const fallbackResponse = await fetch("https://ipapi.co/json/");

      if (fallbackResponse.ok) {
        const fallbackData = await fallbackResponse.json();
        console.log("Fallback API response:", fallbackData);
        return fallbackData.country_code || "US";
      }
    } catch (fallbackError) {
      console.error("Fallback API also failed:", fallbackError);
    }

    // If all methods fail, fall back to browser language detection
    return getBrowserLanguage();
  }
};

/**
 * Get the user's preferred browser language (fallback method)
 * @returns language code (e.g., 'en', 'ua', 'ru')
 */
export const getBrowserLanguage = (): string => {
  // Return default language during SSR (server-side rendering)
  if (typeof navigator === "undefined") return "en";

  // navigator.language returns the primary browser language
  const browserLang =
    navigator.language ||
    (navigator as unknown as LegacyNavigator).userLanguage ||
    "en";

  // Return the language code in lowercase (first 2 characters)
  return browserLang.toLowerCase().substring(0, 2);
};

/**
 * Function to check if the user needs to be redirected to a localized page
 * @param countryCode - user's country code from IP detection
 */
export const getRedirectPathByCountry = (
  countryCode: string,
): string | null => {
  if (!countryCode) return null;

  const upperCaseCountry = countryCode.toUpperCase();

  // Define countries that should be redirected to Ukrainian version
  const ukrainianCountries = ["UA", "BY", "RU"];

  // Define countries that should be redirected to Polish version
  const polishCountries = ["PL"];

  // Define countries that should be redirected to Romanian version
  const romanianCountries = ["RO", "MD"];

  if (ukrainianCountries.includes(upperCaseCountry)) {
    return "/ua";
  }

  if (polishCountries.includes(upperCaseCountry)) {
    return "/pl";
  }

  if (romanianCountries.includes(upperCaseCountry)) {
    return "/ro";
  }

  return null;
};

/**
 * Checks for the presence of a cookie with saved language check information
 */
export const hasLanguageCheckCookie = (): boolean => {
  // Return false during SSR (server-side rendering)
  if (typeof document === "undefined") return false;

  return document.cookie
    .split(";")
    .some((cookie) => cookie.trim().startsWith("countryChecked="));
};

/**
 * Sets a cookie indicating that the language check has been performed
 * @param expirationDays - cookie expiration period in days
 */
export const setLanguageCheckCookie = (expirationDays: number = 30): void => {
  // Skip during SSR (server-side rendering)
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const date = new Date();
  date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;

  // Set domain to base domain without www to work across subdomains
  const hostname = window.location.hostname.replace(/^www\./, "");
  document.cookie = `countryChecked=true;${expires};path=/;SameSite=Strict;domain=.${hostname}`;
};

/**
 * Function to save the language code in a cookie
 * @param languageCode - user's language code
 * @param expirationDays - cookie expiration period in days
 */
export const setLanguageCookie = (
  languageCode: string,
  expirationDays: number = 30,
): void => {
  // Skip during SSR (server-side rendering)
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const date = new Date();
  date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;

  // Set domain to base domain without www to work across subdomains
  const hostname = window.location.hostname.replace(/^www\./, "");
  document.cookie = `userLanguage=${languageCode};${expires};path=/;SameSite=Strict;domain=.${hostname}`;
};

/**
 * Function to retrieve the saved language code from a cookie
 */
export const getLanguageCookie = (): string | null => {
  // Return null during SSR (server-side rendering)
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(new RegExp("(^| )userLanguage=([^;]+)"));
  return match ? match[2] : null;
};

/**
 * Get all preferred browser languages in order of preference
 * @returns array of language codes
 */
export const getBrowserLanguages = (): string[] => {
  // Return empty array during SSR (server-side rendering)
  if (typeof navigator === "undefined") return [];

  if (navigator.languages && navigator.languages.length) {
    // Return all languages, shortened to two-letter code
    return navigator.languages.map((lang) =>
      lang.toLowerCase().substring(0, 2),
    );
  }

  // If the languages array is not available, return the primary language
  return [getBrowserLanguage()];
};

/**
 * Determines the most suitable language for the user
 * from a list of supported languages based on browser preferences
 * @param supportedLanguages array of supported language codes
 * @returns the matching language code or null if no matches found
 */
export const getBestMatchingLanguage = (
  supportedLanguages: string[],
): string | null => {
  const browserLanguages = getBrowserLanguages();

  // Find the first preferred language that is supported
  for (const lang of browserLanguages) {
    if (supportedLanguages.includes(lang)) {
      return lang;
    }
  }

  return null;
};
